import { queryKeys } from '~/composables/api/party/useQueryParty'
import { queryKeys as documentQueryKeys, type QueryDocumentByIdCacheData } from '~/composables/api/document/useQueryDocument'

import { useFetchDocumentParty } from './useFetchDocumentParty'

interface SharedQueryVariables {
  documentId: string
  workspaceId: string
}

const getCommonCallbacks = () => {
  const queryClient = useQueryClient()

  return {
    onMutate: (variables: SharedQueryVariables) => {
      queryClient.cancelQueries({
        queryKey: documentQueryKeys.byId({
          id: variables.documentId,
          workspaceId: variables.workspaceId,
        }).queryKey,
      })
      queryClient.cancelQueries({
        queryKey: queryKeys.all._def,
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.all._def,
      })
    },
    onError: (_: any, variables: SharedQueryVariables) => {
      queryClient.invalidateQueries({
        queryKey: documentQueryKeys.byId({
          id: variables.documentId,
          workspaceId: variables.workspaceId,
        }).queryKey,
      })
    },
  }
}

const create = () => {
  const { create } = useFetchDocumentParty()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: create,
    ...getCommonCallbacks(),
    onSuccess: (responseData, variables) => {
      queryClient.setQueriesData<QueryDocumentByIdCacheData>({
        queryKey: documentQueryKeys.byId({
          id: variables.documentId,
          workspaceId: variables.workspaceId,
        }).queryKey,
      }, (oldData) => {
        if (!oldData)
          return oldData

        return {
          ...oldData,
          data: {
            ...oldData.data,
            parties: [...oldData.data.parties, responseData.data],
          },
        }
      }, {
        updatedAt: Date.now(),
      })
    },
  })
}

const update = () => {
  const { update } = useFetchDocumentParty()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: update,
    ...getCommonCallbacks(),
    onSuccess: (responseData, variables) => {
      queryClient.setQueriesData<QueryDocumentByIdCacheData>({
        queryKey: documentQueryKeys.byId({
          id: variables.documentId,
          workspaceId: variables.workspaceId,
        }).queryKey,
      }, (oldData) => {
        if (!oldData)
          return oldData

        return {
          ...oldData,
          data: {
            ...oldData.data,
            parties: oldData.data.parties.map(p => p.id === variables.partyId ? responseData.data : p),
          },
        }
      }, {
        updatedAt: Date.now(),
      })
    },
  })
}

const drop = () => {
  const { drop } = useFetchDocumentParty()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: drop,
    ...getCommonCallbacks(),
    onSuccess: (_, variables) => {
      queryClient.setQueriesData<QueryDocumentByIdCacheData>({
        queryKey: documentQueryKeys.byId({
          id: variables.documentId,
          workspaceId: variables.workspaceId,
        }).queryKey,
      }, (oldData) => {
        if (!oldData)
          return oldData

        return {
          ...oldData,
          data: {
            ...oldData.data,
            parties: oldData.data.parties.filter(p => p.id !== variables.partyId),
          },
        }
      }, {
        updatedAt: Date.now(),
      })
    },
  })
}

export const useMutationDocumentParty = {
  create,
  update,
  drop,
}
